/**
 * 履约待办 / 节点推进 API（对齐 FulfillmentTaskController / OrderController）。
 *
 * 真实端点（经网关 /api，StripPrefix=1）：
 * - GET  /fulfillment/todo           待办三 Tab（TO_DO / FOLLOWING / DONE）
 * - GET  /fulfillment/todo/count     待办红点计数（仅 TO_DO）
 * - GET  /order/{id}/advance-permission  推进前权限（门控单一事实来源）
 * - GET  /order/{id}/node-action-hint    节点操作提示
 * - PUT  /order/{id}/advance             节点推进
 */
import { request } from './request';
import type {
  TodoItem,
  TaskTab,
  NodeActionPermissionVO,
  NodeActionHintVO,
  AdvanceRequest,
} from './types';

export function getTodo(tab: TaskTab = 'TO_DO'): Promise<TodoItem[]> {
  return request<TodoItem[]>('/fulfillment/todo', { params: { tab } });
}

export function getTodoCount(): Promise<number> {
  return request<number>('/fulfillment/todo/count');
}

export function getAdvancePermission(orderId: string): Promise<NodeActionPermissionVO> {
  return request<NodeActionPermissionVO>(`/order/${orderId}/advance-permission`);
}

export function getNodeActionHint(orderId: string, nodeCode: string): Promise<NodeActionHintVO> {
  return request<NodeActionHintVO>(`/order/${orderId}/node-action-hint`, { params: { nodeCode } });
}

/**
 * 节点推进响应（与 admin 端契约对齐）。
 * 后端 {@code PUT /order/{id}/advance} 可能返回：
 * - {@code true}（成功推进）
 * - {@code {code:'IDEMPOTENT', message, currentNodeCode}}（幂等成功，走 info 提示）
 * - {@code null}（极少数异常）
 * <p>建柜已改由 load 节点 needArchiveCreate 动作入口（onRequestNewArchive）驱动，不再经 NEED_CONTAINER 软阻断。
 */
export type AdvanceNodeResult =
  | boolean
  | { code?: string; message?: string; currentNodeCode?: string }
  | null;

export function advanceOrder(orderId: string, payload: AdvanceRequest): Promise<AdvanceNodeResult> {
  return request<AdvanceNodeResult>(`/order/${orderId}/advance`, { method: 'PUT', body: payload });
}

/**
 * 完成订单（链尾节点终态确认）。
 *
 * <p>FOB/CIF 等短链模板的终点可能是 arrive（观察节点），此时「确认完成」=
 * 完成订单，走 PUT /order/{id}/complete（引擎 complete() 支持任意链尾节点），
 * 而不是 advance()（末节点 nextOf=null 会返回 BLOCKED(LAST_NODE)）。
 */
export function completeOrder(orderId: string): Promise<boolean> {
  return request<boolean>(`/order/${orderId}/complete`, { method: 'PUT' });
}
